import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  SMARTFLOW_FOOTER_IMAGE,
  smartflowHeaderImg,
} from '@common/utils/constants'
import { roundToTwo } from '@common/utils/helperFunctions'
import { deviceWaterUsage } from '@data/waterUsage/model/waterUsage.model'
import { COLUMNS } from '../../WaterUsage/constants'

interface BuildUsageNavigatorPdfArgs {
  chartImageData: string
  deviceName: string
  userUom: string
  waterUsage: deviceWaterUsage[]
}

const HEADER_HEIGHT = 55
const FOOTER_HEIGHT = 40
const CONTENT_START_Y = 60
const CHART_WIDTH = 180
const CHART_HEIGHT = 70
const CHART_PADDING_Y = 10

export const buildUsageNavigatorPdf = ({
  chartImageData,
  deviceName,
  userUom,
  waterUsage,
}: BuildUsageNavigatorPdfArgs): jsPDF => {
  const pdf = new jsPDF('landscape', 'mm', [297, 210])
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const chartX = (pageWidth - CHART_WIDTH) / 2

  autoTable(pdf, {
    head: [['Date', `Usage in ${userUom}`, ...COLUMNS.map((column) => column.name)]],
    body: buildTableRows(waterUsage),
    startY: CONTENT_START_Y,
    tableLineColor: [25, 106, 154],
    styles: {
      fontSize: 6,
      halign: 'center',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [25, 106, 154],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    margin: {
      top: CONTENT_START_Y,
      bottom: FOOTER_HEIGHT,
      left: 2,
      right: 2,
    },
    columnStyles: Object.fromEntries(
      Array.from({ length: 26 }, (_, index) => [index, { cellWidth: 'wrap' }]),
    ),
  })

  const finalY = (pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? CONTENT_START_Y
  const chartY = finalY + CHART_PADDING_Y
  const availableBottomY = pageHeight - FOOTER_HEIGHT

  if (chartY + CHART_HEIGHT > availableBottomY) {
    pdf.addPage()
    pdf.addImage(chartImageData, 'PNG', chartX, CONTENT_START_Y, CHART_WIDTH, CHART_HEIGHT)
  } else {
    pdf.addImage(chartImageData, 'PNG', chartX, chartY, CHART_WIDTH, CHART_HEIGHT)
  }

  addBranding(pdf, deviceName)

  return pdf
}

function buildTableRows(waterUsage: deviceWaterUsage[]) {
  const groupedByDay = waterUsage.reduce<Record<string, { date: string; totalFlow: number; hourValues: Record<number, number | null> }>>(
    (acc, dailyUsage) => {
      const date = dailyUsage.date.split('T')[0]
      const flow = dailyUsage.flow

      if (!acc[date]) {
        acc[date] = {
          date,
          totalFlow: 0,
          hourValues: {},
        }
      }

      if (flow !== null && !Number.isNaN(flow)) {
        acc[date].totalFlow += flow
      }

      acc[date].hourValues[dailyUsage.hour] = flow

      return acc
    },
    {},
  )

  return Object.values(groupedByDay).map((usage) => [
    usage.date,
    roundToTwo(usage.totalFlow),
    ...Array.from({ length: 24 }, (_, hour) => {
      const value = usage.hourValues[hour]
      return value !== null && value !== undefined ? value : '-'
    }),
  ])
}

function addBranding(pdf: jsPDF, title: string) {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageCount = pdf.getNumberOfPages()

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
    pdf.setPage(pageNumber)

    pdf.addImage(smartflowHeaderImg, 'PNG', 0, 0, pageWidth, HEADER_HEIGHT)
    pdf.addImage(
      SMARTFLOW_FOOTER_IMAGE,
      'PNG',
      0,
      pageHeight - FOOTER_HEIGHT,
      pageWidth,
      50,
      undefined,
      'FAST',
    )

    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.setFontSize(8)
    pdf.text(`Page ${pageNumber} of ${pageCount}`, 5, pageHeight - 12)

    if (pageNumber === 1) {
      pdf.setFontSize(12)
      pdf.text(title, 10, 50)
    }
  }
}
